import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../api";
import AuthShell, { GoogleButton, darkInput, darkLabel } from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("ag_email");
    if (saved) setEmail(saved);
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Adresse e-mail invalide.");
    setSubmitting(true);
    try {
      if (remember) localStorage.setItem("ag_email", email);
      else localStorage.removeItem("ag_email");
      await login(email, password);
      navigate("/", { state: { welcome: true }, replace: true });
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Connexion"
      subtitle="Accédez à votre cockpit de gestion automobile."
      footer={<>Pas encore de compte ? <Link to="/inscription" className="font-semibold text-cyan-400 hover:text-cyan-300" data-testid="goto-register-link">Créer un compte</Link></>}
    >
      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label htmlFor="email" className={darkLabel}>Adresse e-mail</Label>
          <Input id="email" type="email" required autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="vous@entreprise.com"
            className={darkInput} data-testid="login-email-input" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className={darkLabel}>Mot de passe</Label>
            <Link to="/mot-de-passe-oublie" className="text-xs font-medium text-cyan-400 hover:text-cyan-300" data-testid="forgot-password-link">
              Mot de passe oublié ?
            </Link>
          </div>
          <Input id="password" type="password" required autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
            className={darkInput} data-testid="login-password-input" />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-400">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-white/20 bg-white/5 accent-cyan-400" data-testid="remember-me-checkbox" />
          Se souvenir de moi
        </label>
        {error && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">{error}</p>
        )}
        <Button type="submit" disabled={submitting}
          className="w-full bg-cyan-500 font-semibold text-[#05070B] hover:bg-cyan-400 transition-colors"
          data-testid="login-submit-button">
          {submitting ? "Connexion…" : "Se connecter"}
        </Button>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="h-px flex-1 bg-white/10" /> ou <span className="h-px flex-1 bg-white/10" />
        </div>
        <GoogleButton />
      </form>
    </AuthShell>
  );
}
